/**
 * MoveWrites — the ONE move ledger: every optimistic write that replaces a
 * target's whole PLACEMENT, on the one ledger engine (`Ledger`).
 *
 * A move names where its target goes, not how it changes: a row's parent
 * and position (`RowAccessor.move`), a held timepoint cell's range
 * (`SheetRow.Handle.moveCell` / `replaceCell` on a row whose cells the SDK's
 * own cache holds). Each move replaces the placement whole, so the target
 * shows the newest move still standing, and the engine's rules are the
 * settle rules of every move:
 *
 * - issue order: `projection = base ⊕ move₁ ⊕ … ⊕ moveₙ` — the newest move
 *   still standing is where the target shows;
 * - accept folds: a landed move joins the base once every older move settled;
 * - a refusal takes back only its own move: the target shows the newest
 *   surviving move, else the base — and a refused move a newer one
 *   superseded changes nothing locally (`refuse` answers `null`);
 * - an authoritative snapshot replaces the base (`confirm`): a placement the
 *   server answered as of move `seq` (a recanonicalized reorder answer). A
 *   move up to `seq` that lands later folds without overwriting it — the
 *   snapshot already holds it, renumbered — and a take-back never returns to
 *   one of them (the placement's `seq` orders it: a move laid over a newer
 *   placement changes nothing).
 *
 * A move's body is its own placement: a later move never carries an older
 * one's change, so the engine's take-back re-send has nothing to correct —
 * each move's transport sends once (`send`) and answers a take-back without
 * a request.
 *
 * It paints nothing and announces nothing itself: `begin` and `refuse`
 * answer what the caller shows (and as whom), because every caller paints
 * its target through its own verb (`SheetHandle.local.move`, the row's cell
 * cache) with its own actor.
 */

import type { Option } from "../core/option.ts";
import { Actor } from "../tx/invalidation.ts";
import { Ledger } from "./ledger.ts";

/** The move ledger over targets `K` placed at `P` (see the module doc). */
export class MoveWrites<K, P> {
  readonly #ledger: Ledger<MoveWrites.Types<K, P>>;
  /** Each move's write in the engine (a move is opaque to its caller). */
  readonly #writes = new WeakMap<MoveWrites.Move<K, P>, Ledger.Write<MoveWrites.Types<K, P>>>();
  readonly #equals: (a: P, b: P) => boolean;
  /** The recorder the engine's one display writes: what the last engine call showed, per target. */
  readonly #shown = new Map<K, MoveWrites.Shown<P>>();
  /** The target's issue counter: a move's `seq` (a snapshot's `confirm` orders by it). */
  #seq = 0;

  /**
   * `name` names the instance in traces (`describe`: a target's trace
   * keys); `equals` compares two placements (a show that changes nothing is
   * not recorded).
   */
  constructor(opts: {
    readonly name: string;
    readonly equals: (a: P, b: P) => boolean;
    readonly describe: (key: K) => { readonly [key: string]: string };
  }) {
    this.#equals = opts.equals;
    const equals = opts.equals;
    const strategy: Ledger.Strategy<MoveWrites.Types<K, P>> = {
      empty: () => ({}),
      merge: (fields, change) => {
        if (change.kind === "none") return fields;
        const held = fields.at;
        return held == null || change.seq > held.seq ? { at: { place: change.place, seq: change.seq } } : fields;
      },
      fieldsOf: (change) => change.kind === "none" ? NO_FIELDS : PLACEMENT,
      body: (fields, only) => (only.has("at") && fields.at != null ? { at: fields.at } : {}),
      adopt: (fields, from, only) => ([...only].includes("at") ? (from.at == null ? {} : { at: from.at }) : fields),
      overlay: (under, over) => (over.at == null ? under : { at: over.at }),
      takeBack: () => NO_MOVE,
      delta: (before, after) => {
        const was = before.at ?? null;
        const now = after.at ?? null;
        if (was == null || now == null) return null;
        return equals(was.place, now.place) ? null : { from: was.place, to: now.place };
      },
      events: () => Ledger.SILENT,
      deferred: () => {
        throw new Error(`${opts.name}: a move defers no announcement`);
      },
      wrap: (err) => (err instanceof Error ? err : new Error(String(err))),
    };
    this.#ledger = new Ledger(strategy, {
      sinks: () => [],
      name: opts.name,
      takeBackContext: `${opts.name}: take-back`,
      describe: opts.describe,
      // A move's transport never re-sends (see the module doc): nothing is abandoned.
      live: () => true,
      abandoned: () => {},
    });
    // The one display records what each call shows; it holds no copy (a
    // move's base is where its caller shows the target when it begins).
    this.#ledger.addDisplay({
      peek: () => null,
      show: (key, delta) => {
        this.#shown.set(key, delta);
        return [];
      },
    }, "first");
  }

  /**
   * Enter a move of `key` from `from` (where the caller shows it now — its
   * base when no move of it is in flight) to `to`. Answers the move and
   * what the caller shows now (`null`: the target already shows `to`).
   */
  begin(key: K, from: P, to: P): { readonly move: MoveWrites.Move<K, P>; readonly show: Option<MoveWrites.Shown<P>> } {
    const seq = ++this.#seq;
    this.#shown.delete(key);
    const write = this.#ledger.begin(key, { kind: "move", place: to, seq }, Actor.EXTERNAL, {
      correction: Actor.EXTERNAL,
      quiet: false,
      capture: () => ({ at: { place: from, seq: 0 } }),
    });
    const move: MoveWrites.Move<K, P> = { key, seq };
    this.#writes.set(move, write);
    return { move, show: this.#take(key) };
  }

  /**
   * Send `move` once through `post` (its own placement is the whole body);
   * resolves with `post`'s answer. Settle it with `accept` / `refuse`.
   */
  send<A>(move: MoveWrites.Move<K, P>, post: () => Promise<A>): Promise<A> {
    const sent: { answer: Option<Promise<A>> } = { answer: null };
    // The engine calls a refused move's transport again for a take-back; a
    // placement has nothing to correct (see the module doc), so a second
    // call answers at once, without a request.
    void this.#ledger.send(this.#write(move), () => {
      if (sent.answer != null) return Promise.resolve(null);
      sent.answer = post();
      return sent.answer;
    });
    if (sent.answer == null) throw new Error("MoveWrites.send: the ledger did not send the move");
    return sent.answer;
  }

  /** `move` landed: it folds into the base once every older move settled. Nothing is shown. */
  accept(move: MoveWrites.Move<K, P>): void {
    this.#ledger.accept(this.#write(move));
  }

  /**
   * `move` was refused (or unanswered): it leaves the ledger. Answers what
   * the caller shows to take it back — from where the target shows now to
   * the newest surviving move, else the base — or `null` when the refused
   * move was not the one shown (a newer move superseded it: nothing changes).
   */
  refuse(move: MoveWrites.Move<K, P>, err: unknown): Option<MoveWrites.Shown<P>> {
    this.#shown.delete(move.key);
    this.#ledger.refuse(this.#write(move), err, "");
    return this.#take(move.key);
  }

  /** `move`'s write in the engine; throws for a move this ledger did not begin. */
  #write(move: MoveWrites.Move<K, P>): Ledger.Write<MoveWrites.Types<K, P>> {
    const write = this.#writes.get(move);
    if (write == null) throw new Error("MoveWrites: a move this ledger did not begin");
    return write;
  }

  /**
   * An authoritative placement of `key` as of move number `seq` (every move
   * up to it applied): it becomes the base while moves of `key` are in
   * flight. Answers where the target shows now — `null` when a move newer
   * than `seq` still stands (it keeps the paint) or no move is in flight.
   */
  confirm(key: K, place: P, seq: number): Option<P> {
    if (this.#ledger.project(key) == null) return null;
    this.#ledger.confirm(key, { at: { place, seq } });
    const shown = this.#ledger.project(key)?.at ?? null;
    return shown == null || shown.seq > seq ? null : shown.place;
  }

  /**
   * The target's life ended (a row's landed delete): every move of `key`
   * in flight is abandoned (`Ledger.forget`) — its late answer, accepted or
   * refused, answers nothing to paint — and nothing is shown. Answers how
   * many moves were abandoned, where the target stood confirmed (`null`:
   * none was in flight) and `landed`: resolves once each was answered, with
   * the placement of the newest that landed after the edge (`null`: none).
   */
  forget(key: K): MoveWrites.Forgotten<P> {
    this.#shown.delete(key);
    const gone = this.#ledger.forget(key);
    return {
      abandoned: gone.abandoned,
      confirmed: gone.confirmed?.at?.place ?? null,
      landed: gone.landed.then((bodies) => bodies.reduce<Option<P>>((at, body) => body.at?.place ?? at, null)),
    };
  }

  /** Where `key` stands confirmed while moves of it are in flight — its base (`null`: none is). */
  confirmed(key: K): Option<P> {
    return this.#ledger.confirmed(key)?.at?.place ?? null;
  }

  /** The issue number of the newest move entered (`0`: none). */
  get issued(): number {
    return this.#seq;
  }

  /** Whether a move of `key` is still in flight. */
  moving(key: K): boolean {
    return this.#ledger.project(key) != null;
  }

  /** Where `key` shows while moves of it are in flight (`null`: none is). */
  shown(key: K): Option<P> {
    return this.#ledger.project(key)?.at?.place ?? null;
  }

  #take(key: K): Option<MoveWrites.Shown<P>> {
    const shown = this.#shown.get(key) ?? null;
    this.#shown.delete(key);
    return shown == null || this.#equals(shown.from, shown.to) ? null : shown;
  }
}

/** Types that go with `MoveWrites`. */
export namespace MoveWrites {
  /** One move in flight (compared by identity); `seq` its issue number. `P`: the placement type it moves. */
  export type Move<K, P> = {
    readonly key: K;
    readonly seq: number;
  };

  /** What `MoveWrites.forget` abandoned: the move count, the confirmed placement (`null`: none in flight) and whether one landed late. */
  export type Forgotten<P> = {
    readonly abandoned: number;
    readonly confirmed: Option<P>;
    readonly landed: Promise<Option<P>>;
  };

  /** What one call shows: the target goes from `from` to `to`. */
  export type Shown<P> = { readonly from: P; readonly to: P };

  /** A placement as the ledger holds it: where, and as of which move (`0`: shown before any). */
  export type At<P> = { readonly place: P; readonly seq: number };

  /** The engine's type bundle for a move ledger. */
  export type Types<K, P> = {
    readonly key: K;
    readonly change: { readonly kind: "move"; readonly place: P; readonly seq: number } | { readonly kind: "none" };
    readonly fields: { readonly at?: At<P> };
    readonly field: "at";
    readonly delta: Shown<P>;
    readonly answer: unknown;
    readonly error: Error;
  };
}

const PLACEMENT: ReadonlySet<"at"> = new Set(["at"]);
const NO_FIELDS: ReadonlySet<"at"> = new Set();
const NO_MOVE = { kind: "none" } as const;
